import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  CheckCircle, XCircle, IdentificationCard, LockKey, Warning,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Matches the step-up token's lifetime, so the roll cannot sit readable on an
// unattended screen after the permission behind it has already lapsed.
const SEAL_AFTER_MS = 10 * 60 * 1000;

/**
 * Registry of Members — the identifying roll, superadmin-only.
 *
 * This is the one view that shows e-mail addresses, which are the only truly
 * identifying data Arcanum holds about a member. It stays locked until the
 * password is re-entered, so a stolen session or an unattended laptop is not
 * enough to read it, and every read is recorded in the audit log.
 */
const MemberRegistry = () => {
  const [members, setMembers] = useState(null);
  // Who has been ASKED into the Sworn and has not answered. Kept beside the
  // roll rather than on it: the registry endpoint says nothing about the
  // circle, and it should stay that way (docs/sworn_design.md §3.1).
  const [invited, setInvited] = useState({});
  const [circleNote, setCircleNote] = useState({});
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const sealTimer = useRef(null);

  // Seal on unmount (leaving the tab) as well as on the timer.
  useEffect(() => () => clearTimeout(sealTimer.current), []);

  const unlock = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/auth/reauth`, { password }, { withCredentials: true });
      setPassword(''); // don't keep it in memory once it has served its purpose
      const { data } = await axios.get(`${API_URL}/api/admin/registry`, { withCredentials: true });
      setMembers(data);
      loadInvitations();
      clearTimeout(sealTimer.current);
      sealTimer.current = setTimeout(() => setMembers(null), SEAL_AFTER_MS);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not open the registry.');
    } finally {
      setBusy(false);
    }
  };

  const relock = () => {
    clearTimeout(sealTimer.current);
    setMembers(null);
    setInvited({});
    setCircleNote({});
    setError('');
  };

  // Live offers only. One that lapsed is gone from here whether it lapsed by
  // silence or by refusal, and that is deliberate -- §7.1 says an invitation
  // that quietly expires must be indistinguishable from one that was declined,
  // so there is nothing to show and nothing to count.
  const loadInvitations = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sworn/invites`, { withCredentials: true });
      const map = {};
      (data || []).forEach((i) => { map[i.user_id] = i; });
      setInvited(map);
    } catch (e) { /* nothing standing, or nothing to say */ }
  };

  const invite = async (id) => {
    setCircleNote((n) => ({ ...n, [id]: '' }));
    try {
      await axios.post(`${API_URL}/api/sworn/invites/${id}`, {}, { withCredentials: true });
      loadInvitations();
    } catch (e) {
      // One answer covers already-sworn, inactive and unknown, because the
      // route refuses to tell them apart -- it would otherwise be a way to
      // test whether a given account is in the circle.
      setCircleNote((n) => ({ ...n, [id]: 'Not available' }));
    }
  };

  const withdrawInvite = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/sworn/invites/${id}`, { withCredentials: true });
    } catch (e) { /* already answered or lapsed */ }
    loadInvitations();
  };

  const fmtDate = (src) => {
    if (!src) return '—';
    try { return format(new Date(src), 'd MMM yyyy'); } catch (e) { return '—'; }
  };

  const header = (
    <div className="flex items-center gap-3">
      <IdentificationCard className="w-6 h-6" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
      <div>
        <h2 className="text-xl font-heading font-semibold">Registry of Members</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          {members
            ? `${members.length} member${members.length === 1 ? '' : 's'} admitted · by number`
            : 'Sealed — confirm your password to open'}
        </p>
      </div>
    </div>
  );

  // ---- sealed ----
  if (!members) {
    return (
      <div className="space-y-4" data-testid="member-registry">
        {header}

        <form
          onSubmit={unlock}
          className="max-w-sm space-y-3 border border-border rounded-lg p-5 bg-card"
          data-testid="registry-unlock-form"
        >
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <LockKey className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
            <span>
              The roll carries the members' e-mail addresses — the only identifying
              data the Society holds. Opening it is recorded in the audit log.
            </span>
          </div>

          <Input
            type="password"
            placeholder="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            className="h-11"
            data-testid="registry-password"
          />

          {error && (
            <div className="flex items-start gap-2 text-sm text-destructive" data-testid="registry-error">
              <Warning className="w-4 h-4 mt-0.5 shrink-0" weight="fill" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={busy || !password}
            className="w-full bg-primary hover:bg-primary/90"
            data-testid="registry-unlock"
          >
            {busy ? 'Opening…' : 'Open the registry'}
          </Button>
        </form>
      </div>
    );
  }

  // ---- open ----
  return (
    <div className="space-y-4" data-testid="member-registry">
      <div className="flex items-start justify-between gap-4">
        {header}
        <Button variant="outline" size="sm" onClick={relock} data-testid="registry-relock">
          <LockKey className="w-4 h-4 mr-2" /> Seal again
        </Button>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">No.</TableHead>
              <TableHead>Member</TableHead>
              <TableHead>Identifier</TableHead>
              <TableHead>Admitted</TableHead>
              <TableHead>Standing</TableHead>
              <TableHead className="text-right">The Sworn</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No members admitted yet.
                </TableCell>
              </TableRow>
            ) : (
              members.map((m) => (
                <TableRow key={m.id} data-testid={`registry-row-${m.id}`}>
                  <TableCell
                    className="font-heading text-base tabular-nums"
                    style={{ color: 'hsl(var(--gold))' }}
                  >
                    {m.member_no != null ? String(m.member_no).padStart(3, '0') : '—'}
                  </TableCell>
                  <TableCell className="font-medium">{m.display_name || m.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{m.email}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(m.admitted_at || m.created_at)}</TableCell>
                  <TableCell>
                    {m.is_active ? (
                      <Badge variant="outline" className="border-green-500/20 bg-green-500/10 text-green-400">
                        <CheckCircle className="w-3 h-3 mr-1" weight="fill" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-destructive/20 bg-destructive/10 text-destructive">
                        <XCircle className="w-3 h-3 mr-1" weight="fill" />
                        Inactive
                      </Badge>
                    )}
                  </TableCell>
                  {/* Offered, not assigned (§7). The button asks; it does not
                      admit. Nothing here reports a refusal, because there is
                      nothing stored that could. */}
                  <TableCell className="text-right whitespace-nowrap">
                    {circleNote[m.id] ? (
                      <span className="text-xs text-muted-foreground">{circleNote[m.id]}</span>
                    ) : invited[m.id] ? (
                      <span className="text-xs text-muted-foreground">
                        Asked
                        <button
                          type="button"
                          onClick={() => withdrawInvite(m.id)}
                          className="ml-2 underline hover:text-foreground"
                          data-testid={`sworn-withdraw-${m.id}`}
                        >
                          withdraw
                        </button>
                      </span>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => invite(m.id)}
                        data-testid={`sworn-invite-${m.id}`}
                      >
                        Invite
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground">
        This view stays open for ten minutes, then seals itself.
      </p>
    </div>
  );
};

export default MemberRegistry;
